import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { OverlayPanel } from "primereact/overlaypanel";
import ReusableForm from "../common/ReusableForm";
import ReusableTable from "../dashboard/ReusableTable";
import { getRequest, postRequest } from "../services/apiService";
import ReusableDeleteDialog from '../common/ReusableDeleteDialog';

const SubTaskActionCell = ({ rowData, onEdit, onDelete }) => {
  const op = useRef(null);
  const menuItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: () => {
        op.current.hide();
        onEdit(rowData);
      }
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: () => {
        op.current.hide();
        onDelete(rowData);
      }
    }
  ];

  return (
    <div className="relative">
      <Button
        icon="pi pi-ellipsis-h"
        className="p-button-rounded p-button-text p-2"
        onClick={(e) => op.current.toggle(e)}
      />
      <OverlayPanel 
        ref={op} 
        className="p-0 shadow-lg"
        style={{ zIndex: 9999 }}
        appendTo={typeof document !== 'undefined' ? document.body : null}
      >
        <Menu model={menuItems} className="border-none w-40" />
      </OverlayPanel>
    </div>
  );
};

const SubTaskDialog = ({ visible, onHide, taskData, fetchTasks, toast, isModalOverlay }) => {
  const [subTasks, setSubTasks] = useState([]);
  const [subTasksFirst, setSubTasksFirst] = useState(0);
  const [subTasksRows, setSubTasksRows] = useState(5);
  const [subTasksTotalRecords, setSubTasksTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editingSubTask, setEditingSubTask] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [subTaskToDelete, setSubTaskToDelete] = useState(null);
  const [deleteFormData, setDeleteFormData] = useState({ confirmation: "" });
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const modalRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    contribution: "10",
    parent_task: taskData?.id || null,
    status_db: 1
  });

  const subTaskFormFields = [
    { 
      name: "name", 
      label: "Sub Task Name", 
      type: "text", 
      placeholder: "Enter sub task name", 
      required: true 
    },
    { 
      name: "contribution", 
      label: "Contribution (%)", 
      type: "text", 
      placeholder: "Enter contribution percentage", 
      required: true 
    }
  ];

  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirmation",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  const columns = [
    { field: "name", header: "Sub Task Name", sortable: true, style: { minWidth: '200px' } },
    { 
      field: "contribution", 
      header: "Contribution (%)", 
      sortable: true,
      body: (rowData) => `${rowData.contribution}%`,
      style: { minWidth: '150px' }
    },
    {
      field: "actions",
      header: "Actions",
      body: (rowData) => (
        <SubTaskActionCell rowData={rowData} onEdit={handleEditSubTask} onDelete={handleDeleteSubTask} />
      ),
      notFilterable: true,
      style: { width: '120px', textAlign: 'center' }
    }
  ];

  const normalizeResponse = (data) => {
    if (Array.isArray(data)) return data;
    if (data.results && Array.isArray(data.results.data)) return data.results.data;
    if (data.data && Array.isArray(data.data)) return data.data;
    return [];
  };

  // Fetch sub tasks for the current task
  const fetchSubTasks = async () => {
    if (!visible || !taskData?.id) return;
    setLoading(true);
    try {
      const response = await getRequest(`/master/tasks/?parent_task=${taskData.id}&search=${encodeURIComponent(search)}`);
      const subTasksData = normalizeResponse(response);
      setSubTasks(subTasksData);
      setSubTasksTotalRecords(subTasksData.length);
    } catch (e) {
      // handle error
    } finally {
      setLoading(false);
    }
  };

  // Debounced search effect for sub tasks
  useEffect(() => {
    fetchSubTasks();
    // eslint-disable-next-line
  }, [visible, taskData, search]);

  useEffect(() => {
    if (visible) {
      resetForm();
    }
  }, [visible, taskData]);

  // Update subTasksTotalRecords if subTasks change
  useEffect(() => {
    setSubTasksTotalRecords(subTasks.length);
  }, [subTasks]);

  // Handle page change for subTasks
  const onSubTasksPageChange = (event) => {
    setSubTasksFirst(event.first);
    setSubTasksRows(event.rows);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      contribution: "10",
      parent_task: taskData?.id || null,
      status_db: 1
    });
    setEditingSubTask(null);
  };

  const handleSaveSubTask = async () => {
    if (!formData.name) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Sub Task name is required",
        life: 3000
      });
      return;
    }

    const payload = {
      ...formData,
      contribution: parseFloat(formData.contribution || 10),
      parent_task: taskData?.id || null
    };

    setLoading(true);
    try {
      if (editingSubTask) {
        // UPDATE: Use POST with id in payload
        await postRequest(`/master/tasks/`, {
          ...payload,
          id: editingSubTask.id
        });
        toast.current?.show({ 
          severity: "success", 
          summary: "Success", 
          detail: "Sub Task updated successfully", 
          life: 3000 
        });
        await fetchSubTasks();
      } else {
        // CREATE: Normal POST request
        await postRequest("/master/tasks/", payload);
        toast.current?.show({ 
          severity: "success", 
          summary: "Success", 
          detail: "Sub Task added successfully", 
          life: 3000 
        });
        await fetchSubTasks();
      }
      fetchTasks();
      resetForm();
    } catch (error) {
      console.error("Error saving sub task:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to ${editingSubTask ? "update" : "add"} sub task`,
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubTask = (subTask) => {
    setEditingSubTask(subTask);
    setFormData({
      name: subTask.name || "",
      contribution: subTask.contribution != null ? String(subTask.contribution) : "10",
      parent_task: subTask.parent_task || taskData?.id || null,
      status_db: subTask.status_db != null ? subTask.status_db : 1
    });
    setShowEditDialog(true);
  };

  const closeEditDialog = () => {
    setShowEditDialog(false);
    setEditingSubTask(null);
    resetForm();
  };

  const handleDeleteSubTask = (subTask) => {
    setSubTaskToDelete(subTask);
    setDeleteFormData({ confirmation: "" });
    setShowDeleteDialog(true);
  };

  const confirmDeleteSubTask = async () => {
    if (deleteFormData.confirmation !== "DELETE") {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please type 'DELETE' to confirm",
        life: 3000
      });
      return;
    }
    try {
      // SOFT DELETE: Use POST with status_db=3
      await postRequest(`/master/tasks/`, {
        id: subTaskToDelete.id,
        status_db: 3
      });
      toast.current?.show({ 
        severity: "success", 
        summary: "Success", 
        detail: "Sub Task deleted successfully", 
        life: 3000 
      });
      await fetchSubTasks();
      fetchTasks();
      setShowDeleteDialog(false);
      setSubTaskToDelete(null);
      setDeleteFormData({ confirmation: "" });
    } catch (error) {
      console.error("Error deleting sub task:", error);
      toast.current?.show({ 
        severity: "error", 
        summary: "Error", 
        detail: "Failed to delete sub task", 
        life: 3000 
      });
    }
  };

  const toggleFullscreen = () => {
    const elem = modalRef.current;
    if (!elem) return;
    if (!document.fullscreenElement && !document.mozFullScreenElement && !document.webkitFullscreenElement && !document.msFullscreenElement) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!(document.fullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement || document.msFullscreenElement));
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('mozfullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    document.addEventListener('msfullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('mozfullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
      document.removeEventListener('msfullscreenchange', onFullscreenChange);
    };
  }, []);

  const mainDialogContent = (
    <div className="p-fluid">
      {taskData && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <p className="font-medium text-gray-800">
            Parent Task: <span className="text-blue-600">{taskData.name}</span>
          </p>
        </div>
      )}

      <ReusableForm
        formData={formData}
        setFormData={setFormData}
        fields={subTaskFormFields}
        onSubmit={handleSaveSubTask}
        loading={loading}
        submitLabel={editingSubTask ? "Update Sub Task" : "Add Sub Task"}
        buttonClassName="p-1"
      />

      <div className="mt-4">
        <ReusableTable
          data={subTasks.slice(subTasksFirst, subTasksFirst + subTasksRows)}
          columnFields={columns}
          loading={loading}
          rows={subTasksRows}
          totalRecords={subTasksTotalRecords}
          first={subTasksFirst}
          onPageChange={onSubTasksPageChange}
          emptyMessage="No sub tasks found for this task"
          searchValue={search}
          onSearchChange={setSearch}
          tableStyle={{ minWidth: '600px' }}
        />
      </div>
    </div>
  );
  
  if (showEditDialog) {
    return (
      <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black bg-opacity-40">
        <div className="bg-white rounded-xl shadow-2xl w-full mx-4 animate-fade-in max-w-lg">
          <ReusableForm
            formData={formData}
            setFormData={setFormData}
            fields={subTaskFormFields}
            onSubmit={async () => {
              await handleSaveSubTask();
              closeEditDialog();
            }}
            onHide={closeEditDialog}
            loading={loading}
            headerText={`Edit Sub Task - ${editingSubTask?.name || ""}`}
            submitLabel="Update Sub Task"
          />
        </div>
      </div>
    );
  }

  if (isModalOverlay) {
    return (
      <div ref={modalRef} className="bg-white rounded-xl shadow-2xl w-full flex flex-col h-[90vh] max-h-[700px]">
        <div className="flex items-center justify-between px-6 py-4 bg-[#1e3a8a] rounded-t-xl border-b border-gray-200 sticky top-0 z-10">
          <h3 className="text-xl font-bold text-white tracking-wide">Manage Sub Tasks for: {taskData?.name || "Task"}</h3>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              icon={isFullscreen ? "pi pi-window-minimize" : "pi pi-window-maximize"}
              onClick={toggleFullscreen}
              className="p-button-text p-button-secondary text-white"
              tooltip="Toggle Fullscreen"
              tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              icon="pi pi-times"
              className="p-button-text p-button-rounded text-white"
              onClick={() => {
                resetForm();
                onHide();
              }}
            />
          </div>
        </div>
        <div className="overflow-y-auto flex-grow p-6">
          {mainDialogContent}
        </div>
        <ReusableDeleteDialog
          visible={showDeleteDialog}
          onHide={() => setShowDeleteDialog(false)}
          onDelete={confirmDeleteSubTask}
          fields={deleteFormFields}
          formData={deleteFormData}
          setFormData={setDeleteFormData}
          loading={loading}
          title="Delete Sub Task"
          description={`Are you sure you want to delete sub task "${subTaskToDelete?.name}"? This action cannot be undone. Type 'DELETE' to confirm.`}
          deleteLabel="Delete"
        />
      </div>
    );
  }
  
  return (
    <>
      <Dialog
        visible={visible}
        headerClassName="bg-[#1e3a8a] text-white px-6 py-4 rounded-t-lg"
        header={
          <div className="flex justify-between items-center">
            <span>Manage Sub Tasks for {taskData?.name || "Task"}</span>
          </div>
        }
        onHide={() => {
          resetForm();
          onHide();
        }}
        style={{ width: "700px" }}
        modal
      >
        {mainDialogContent}
      </Dialog>

      <ReusableDeleteDialog
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        onDelete={confirmDeleteSubTask}
        fields={deleteFormFields}
        formData={deleteFormData}
        setFormData={setDeleteFormData}
        loading={loading}
        title="Delete Sub Task"
        description={`Are you sure you want to delete sub task "${subTaskToDelete?.name}"? This action cannot be undone. Type 'DELETE' to confirm.`}
        deleteLabel="Delete"
        className="z-50"
      />
    </>
  );
};

export default SubTaskDialog;